// Created: 2026-08-14
// Updated: 2026-09-03
//
// The single source of key truth for the author/public PROFILE section
// (react-query-profile-collections, Req 1).
//
// Inherited conventions (slice 1 `queries/material/keys.ts`, slice 2 `queries/pattern/keys.ts`, slice 3
// `queries/craftProject/keys.ts`, slice 4 `queries/browse/keys.ts`): keys are hierarchical arrays of
// JSON-serializable primitives and plain objects, element 0 names the resource, PERSONAL data is
// namespaced under a leading 'me' and its query is always `enabled: !!idToken`. A parameter belongs in a
// key only when it selects a DIFFERENT resource, not when it merely filters one.
//
// THE STRUCTURAL FACT behind every PUBLIC key below, established from code rather than assumed:
// **`/api` has no authorizer.** `infra/cdk/src/constructs/api-construct.ts:49` is
// `apiResource.addProxy({ defaultIntegration: …, anyMethod: true })` with NO `authorizationType`; only
// `/api/secure` (lines 75 and 92) is `AuthorizationType.COGNITO`. So `event.requestContext.authorizer`
// is ABSENT on every `/api` read no matter what the handler tries to read from it, and none of the five
// public profile fetchers sends an `Authorization` header. Concretely:
//   - `get-profile-page.ts:23` DOES read `event.requestContext.authorizer?.claims?.sub` into
//     `viewerUserId` and passes it on as `requestingUserId` — it is always `undefined`, so
//     `packages/lambda-utilities/src/utils/shared-profile-fetcher.ts:112`
//     `isOwnProfile = requestingUserId === identity_id` is ALWAYS false and the owner-only field block
//     never runs.
//   - the three paginated social handlers (`get-user-favorites-paginated.ts:49`,
//     `get-user-followers-paginated.ts:49`, `get-user-following-paginated.ts:49`) do the same, so their
//     `isOwner` is always false too — which is why a `private` privacy setting yields
//     `{items: [], lastKey: null}` for EVERY caller including the owner. That is a pre-existing
//     consequence this slice PRESERVES, not a bug it introduces (Req 2.4, 9.8).
//   - `get-patterns-by-author.ts` reads no authorizer at all and queries
//     `begins_with(GSI3SK, 'public#')` — the visitor view only.
// The same bytes therefore serve every caller, which is what makes these keys safe to share and, later,
// safe for the SSG/ISR step to dehydrate→hydrate-seed.
//
// IF THAT EVER STOPS HOLDING — an authorizer appears on the public proxy, a handler starts reading an
// `Authorization` header, or one of these fetchers starts sending one — THEN the identity-varying data
// MUST move under a `['me', …]` key, or the affected key MUST gain an identity discriminator, in the
// SAME change (Req 1.6). Otherwise one shared entry would serve one caller's profile to another.
//
// NO CURSOR APPEARS IN ANY KEY. The four continuations carry theirs in `pageParam` — all pages of one
// infinite query live under ONE cache entry, so keying a cursor would mint an entry per page and defeat
// accumulation (slice 4 LIST rule 1). See `queries.ts`.
//
// A LISTING BUCKET, on the other hand, DOES appear in a key, and the distinction is the convention
// working rather than an exception to it: a cursor names a POSITION inside one resource, while a bucket
// names WHICH resource — a different key range, and for the archived bucket a different route
// (content-visibility-model Requirement 5.3, task 29). See `authorPatterns` and `myPatternBucket`.

// The Listing_Bucket vocabulary, imported rather than restated. `api/listingBuckets.ts` records why the
// four tokens are derived from the `Visibility` enum plus the archived Shelf_State rather than spelled
// out, and why the website cannot import the backend authority directly.
import { DEFAULT_LISTING_BUCKET, type ListingBucket } from '../../api/listingBuckets'

/**
 * Stale window for every profile read — applied as a per-query override, never as a client default.
 *
 * The shared 5-minute baseline (`DEFAULT_STALE_TIME_MS`) is wrong here. An author's own pattern list
 * changes when they PUBLISH, and publishing is slice 2/3 territory: those mutations invalidate
 * `patternKeys` / `craftProjectKeys`, NOT `profileKeys`, and nothing in THIS slice invalidates a profile
 * key either. So with the baseline, an author who publishes, navigates to their own profile, and looks
 * for the new pattern could miss it for five minutes and reasonably conclude the publish failed.
 *
 * Unlike search there is no indexing excuse to hide behind: the source is a DynamoDB GSI and is
 * immediately consistent, so the user's expectation is strictly higher than it is for OpenSearch.
 *
 * 30 s keeps the back-navigation win (leaving a profile and returning inside one visit is instant, no
 * request) while bounding staleness to well under the human "did that land?" loop (Req 7.1, 7.4).
 *
 * Declared SEPARATELY from `COLLECTION_STALE_TIME_MS` on purpose — see the note there.
 */
export const PROFILE_STALE_TIME_MS = 30_000

/**
 * Every profile query key in the app. Element 0 names the resource; no other module builds one.
 *
 * `screenName` is keyed VERBATIM, not lower-cased. Slice 4's normalizers exist because a search term or
 * a filter object has many spellings of ONE request; a screen name is a path segment that
 * `encodeURIComponent` puts on the wire as-is and that the handler resolves through
 * `getIdentityIdByScreenName`. The Owner_Branch comparison in `Profile.tsx` lower-cases *for the branch
 * decision only* — the URL still sends the raw segment. Lower-casing the key would make it describe a
 * request that never went out, which is exactly the key↔URL drift slice 4's "normalize before keying,
 * and pass the normalized value to the fetcher too" rule forbids. So: verbatim, and `'Alice'` and
 * `'alice'` are two entries because they are two requests.
 */
export const profileKeys = {
  /**
   * PUBLIC — the whole author profile page payload (profile + page 1 of four lists + four cursors).
   * Seedable / dehydratable for the later SSG/ISR step.
   *
   * Identity-invariance evidence: `fetchUserProfilePage` sends no `Authorization`; `/api` carries no
   * authorizer; `get-profile-page.ts:23`'s `viewerUserId` is therefore always `undefined` and
   * `shared-profile-fetcher.ts:112` `isOwnProfile` is always false. See the module header for the
   * "if this stops holding" conditional, which applies to every PUBLIC key here.
   */
  authorProfile: (screenName: string) => ['authorProfile', screenName] as const,

  /**
   * PUBLIC — continuation pages of the author's patterns, FOR ONE LISTING BUCKET.
   *
   * Identity-invariance evidence: `fetchAuthorPatterns` sends no header and
   * `get-patterns-by-author.ts` reads no authorizer; its query is the `public#` bucket, i.e. the
   * visitor view only.
   *
   * ## The bucket is in the key, and that is a correctness requirement rather than tidiness
   *
   * The owner's patterns listing is now FOUR listings, one per Listing_Bucket
   * (content-visibility-model Requirement 5.3, task 29), and a continuation cursor names a position
   * inside ONE range scan. Under a single shared key the accumulated pages of the Unlisted segment
   * would still be in the entry when the owner switched to Private, so the second segment would render
   * the first segment's rows beneath its own page one — and a cursor minted in one bucket could be
   * handed to a query answering another. Keying the bucket makes both impossible by construction:
   * switching segments switches cache entries, so there is nothing to reuse and nothing to reset.
   *
   * `authorPatternsPrefix` below is what the per-mount reset targets, so keying the bucket did not turn
   * one `removeQueries` into four.
   *
   * **This key now serves the VISITOR branch only** (content-visibility-model task 49). A visitor sees
   * one bucket — `public` — because the route behind this key queries only that range, so in practice
   * this entry is `['authorPatterns', sn, 'public']`. The bucket parameter is kept because the guarantee
   * wanted here is that a cursor cannot cross buckets, and that must not depend on which buckets a
   * particular caller happens to be able to ask for.
   *
   * The OWNER's four buckets moved to `myPatternBucket` below, which is a different ROUTE
   * (`GET /api/secure/patterns?bucket=`) as well as a different audience. Task 29 had the owner's public
   * segment continue through THIS key, because the anonymous route was the only continuation there was;
   * that is no longer true, and an owner's cursor must not sit under an identity-invariant key in any
   * case.
   */
  authorPatterns: (screenName: string, bucket: ListingBucket = DEFAULT_LISTING_BUCKET) =>
    ['authorPatterns', screenName, bucket] as const,

  /**
   * The PREFIX the per-mount continuation reset removes, so ONE call reaches every bucket's entry.
   *
   * React Query matches by prefix, so `['authorPatterns', screenName]` reaches
   * `['authorPatterns', screenName, 'public']` and its three siblings. That is what keeps the
   * `Profile.tsx` reset — the load-bearing one that stops a disabled infinite query serving a previous
   * visit's appended pages — a single call rather than one per bucket, which is the shape
   * `collectionKeys.myListPrefix` already established for the same reason.
   */
  authorPatternsPrefix: (screenName: string) => ['authorPatterns', screenName] as const,

  /**
   * PUBLIC — continuation pages of the author's favorites.
   *
   * Identity-invariance evidence: no header sent; `get-user-favorites-paginated.ts:49` reads
   * `authorizer?.claims?.sub`, which is always `undefined`, so `isOwner` is always false.
   */
  authorFavorites: (screenName: string) => ['authorFavorites', screenName] as const,

  /** PUBLIC — continuation pages of the author's followers. Same evidence as `authorFavorites`. */
  authorFollowers: (screenName: string) => ['authorFollowers', screenName] as const,

  /** PUBLIC — continuation pages of who the author follows. Same evidence as `authorFavorites`. */
  authorFollowing: (screenName: string) => ['authorFollowing', screenName] as const,

  /**
   * PERSONAL — the OWNER's own profile payload. Never dehydrated into shared HTML.
   *
   * This one genuinely varies by caller, which is why it is `'me'`-namespaced and its query is
   * `enabled: !!idToken`: `lambda-secure/src/routes/get-secure-profile.ts:16` resolves the caller with
   * `getIdentityId(event)` and returns **401** without it, passes `includePrivatePatterns: true`, and
   * attaches the owner-only `unpublishedDrafts` and `permissions` alongside the owner-only
   * `private_pattern_count`, `archived_pattern_count`, `private_collection_count`, and
   * `archived_collection_count` fields. Serving any of that from `authorProfile` would leak an owner's
   * private counts and drafts to every visitor.
   *
   * `screenName` is still part of the key even though the caller is implied by the token: the route is
   * `/api/secure/profile/{screenName}`, so the screen name selects WHICH profile is asked for.
   */
  myProfile: (screenName: string) => ['me', 'profile', screenName] as const,

  /**
   * PERSONAL — ONE Listing_Bucket of the OWNER's own patterns, fetched lazily when its segment opens
   * (content-visibility-model Requirements 5.3, 5.4, 12.1, 12.3; task 29).
   *
   * ## Why this is not served from `authorProfile`, and why that is the point
   *
   * `keys.ts` has always recorded that the owner payload carries `private_pattern_count`,
   * `archived_pattern_count`, `private_collection_count` and `archived_collection_count`, and that
   * serving any of it from the public `authorProfile` entry would leak an owner's counts to every
   * visitor. The same argument applies with more force to the ITEMS: an Unlisted or Private bucket
   * listing is content a visitor may not see at all, so it cannot share a cache entry with a key that
   * is identity-invariant, shared between callers, and a candidate for dehydration into CDN-cached
   * HTML by the later SSG/ISR step.
   *
   * So these entries are `'me'`-namespaced, their query is `enabled: !!idToken`, and eviction on
   * sign-out is inherited rather than added: `AuthContext`'s PERSONAL_PURGE is
   * `removeQueries({ queryKey: ['me'] })` (BL-002), which prefix-matches every bucket entry here with
   * no new purge site to keep in agreement with it. A public `['authorProfile', …]` invalidation cannot
   * reach them either, in either direction.
   *
   * ## Why the bucket earns a place in the key
   *
   * It selects a DIFFERENT resource, not a filter over one: `?bucket=unlisted` is a different key
   * condition over a different key range. Four buckets are therefore four entries, which is also what
   * makes switching back to a visited segment instant.
   *
   * ## What changed in task 49, and what it means for this entry
   *
   * All four buckets are now served by ONE route, `GET /api/secure/patterns?bucket=&lastKey=`, and this
   * entry holds an INFINITE query rather than a single page: page one plus every continuation page of the
   * selected bucket. Before task 49 the same key held one page, fetched by re-requesting the whole
   * profile envelope for `unlisted`/`private` and `GET /api/secure/pattern/archived` for `archived`, and
   * none of the three could page at all.
   *
   * The cursor is in `pageParam` and never in this key, per the module header — all pages of one bucket
   * live under this one entry, which is what makes accumulation work. The BUCKET is in the key, so a
   * cursor minted in one bucket can never be handed to a query answering another; the route refuses such
   * a cursor with a 400 as well, so the guarantee holds at both ends.
   *
   * `screenName` stays in the key even though the route no longer takes one: the caller is implied by the
   * token, and keying the screen name keeps this entry aligned with `myProfile`'s — the envelope that
   * serves the public segment's page one — so the per-mount reset and the branch decision address the
   * same profile. It also means a second account signing in inside one tab cannot read the first's
   * entry, over and above the sign-out purge.
   *
   * PRERENDERABLE: never. Same reasoning as `myProfile`, one step stronger.
   */
  myPatternBucket: (screenName: string, bucket: ListingBucket) =>
    ['me', 'profile', screenName, 'patterns', bucket] as const,

  /**
   * The PREFIX the per-mount continuation reset removes for the OWNER's buckets, so ONE call reaches all
   * four entries — the `['me', …]` twin of `authorPatternsPrefix`.
   *
   * Load-bearing for the same reason: the PUBLIC bucket's owner entry is a DISABLED infinite query (its
   * page one comes from the envelope), and a disabled query does not refetch on mount, so without this
   * its accumulated pages would survive a remount for the whole `gcTime` and a fresh visit would render
   * the envelope's page one followed by the previous visit's appended pages.
   *
   * It is deliberately NOT `myProfile(sn)`: that prefix would also remove the envelope entry itself,
   * throwing away the profile, the counters, the drafts and the four social lists on every mount.
   */
  myPatternBucketPrefix: (screenName: string) => ['me', 'profile', screenName, 'patterns'] as const,

  /**
   * PERSONAL — continuation pages of the OWNER's OWN favorites, read through
   * `GET /api/secure/user-favorites/{screenName}` (content-visibility-model tasks 47/48, BL-322).
   *
   * ## Why favorites needs a second key when the other three continuations do not
   *
   * `authorFavorites` above is identity-invariant and stays exactly as it is: the anonymous route
   * answers the same bytes to everybody. This entry is NOT the same resource read with more privilege —
   * it is a different SEQUENCE. For the surface owner, a favourited pattern whose author has since made
   * it private renders as a MASKED entry (Requirement 9.6); for a third party it is omitted entirely.
   * So the owner's list and the visitor's list have different members, different lengths and different
   * page boundaries, and a cursor is bound to one audience or the other — `fillPage` refuses it across
   * that boundary with a 400.
   *
   * Two sequences cannot share one cache entry. And this one must be `'me'`-namespaced rather than
   * merely distinct, because it holds the owner's view: `AuthContext`'s
   * `removeQueries({ queryKey: ['me'] })` (BL-002) then evicts it on sign-out with no purge site of its
   * own, and no public invalidation can prefix-match it. This is the identical resolution
   * `queries/authenticatedFallback.ts` reached for the authenticated pattern read, applied to a list.
   *
   * PRERENDERABLE: never.
   */
  myFavorites: (screenName: string) => ['me', 'profile', screenName, 'favorites'] as const,
}

/** The union of every key this factory emits — used by the key tests. */
export type ProfileKey = ReturnType<(typeof profileKeys)[keyof typeof profileKeys]>
